import { useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { ToggleButtonGroup, ToggleButton } from "react-bootstrap"

import useT from "utils/hooks/useT"
import { Loading, Popup } from "components"
import { AnalysisTaskConfig } from "components/Config"
import { SampleTableForPlannedAnalysis } from "components/SampleTable"
import { analysisTasks, analyses, permissions } from "state_management"

// The analysis-task popup — the three-tab dialog ("View analysis samples" / "Edit analysis task" /
// "Result entry") that opens on top of whatever the user is looking at.
//
// RR8 #4061: this used to live INLINE inside `ScheduleElement`, i.e. inside a plan cell, which meant
// it could only exist while the plan was mounted AND the target task happened to be rendered in that
// particular member+date cell. A "Go to analysis" from Sample View therefore had to route to the plan
// first just so a cell existed to host the dialog. Extracted here so BOTH entry points share ONE
// implementation:
//   * `ScheduleElement`      — a click on a task tile on the plan.
//   * `AnalysisTaskHost`     — a programmatic open (`visual.activeAnalysisTaskId`), from Sample View,
//                              the QA worklist, or a Reviews card — route-independent.
// One copy, deliberately: the duplicated-render-logic mistake is what let RR8 #4063 and #4068 be two
// separate bug reports for one root cause.
//
// Props:
//   taskId               — the analysis-task id (also the remount key the caller should rely on)
//   task                 — the resolved task object (may be a caller-held snapshot, see SPEC-028)
//   focusQc              — open the Result-entry TAB (SPEC-060 #4132; was: expand + scroll to the
//                          "QC Results & Approvals" section — SPEC-022 K2)
//   initialTab           — "TASK" (Edit analysis task) or "SAMPLE" (View analysis samples). The QC
//                          tab is reached via `focusQc`, not by asking for it here, because that is
//                          how every existing deep-link already expresses "show me QC" (SPEC-060)
//   bookedAndFreeAssets  — asset availability for the task form; caller fetches it
//   loading              — the caller is still resolving what goes inside (RR8 #4061): the dialog
//                          appears IMMEDIATELY on the user's click and shows a spinner in its body
//                          until the content is ready, rather than the click doing nothing visible
//                          while an asset lookup runs
//   disabled             — render read-only
//   onClose              — the caller clears its own open-state here
export default function AnalysisTaskPopup({
  taskId,
  task,
  focusQc = false,
  initialTab = "TASK",
  bookedAndFreeAssets,
  loading = false,
  disabled = false,
  onClose,
}) {
  const { t } = useT("common")
  const dispatch = useDispatch()
  // Which tab is showing. Seeded from the caller's intent and then owned here, so the tab is not a
  // piece of state every caller has to carry. SPEC-060 (#4132): three tabs now, so this is a tab
  // id rather than a boolean. `focusQc` lands straight on the QC tab — that is what its callers
  // (Sample View, the QA worklist, a Reviews card) are asking for.
  // SPEC-060 (#4132): the Result-entry tab exists only for someone with a part in the QC result
  // flow (analyst / reviewer / supervisor / qa — NOT qa-manager), on a LIMS team, for a PLANNED
  // analysis. Anyone else has nothing to see there, so the tab is absent rather than ghosted.
  const showLims = useSelector((state) => state.teamStatus.showLims)
  const hasQcRole = useSelector(permissions.selectHasQcResultFlowRole)
  const showQcTab = !!showLims && hasQcRole && !!(task && task.analysisId)

  const [activeTab, setActiveTab] = useState(() => {
    if (initialTab === "SAMPLE") return "SAMPLE"
    return focusQc && showQcTab ? "QC" : "TASK"
  })
  // A deep link can ask for QC on a task whose tab is not available (no QC role, LIMS off, or the
  // task resolved late); fall back to the task form rather than rendering an empty tab.
  const effectiveTab = activeTab === "QC" && !showQcTab ? "TASK" : activeTab

  if (!taskId || (!task && !loading)) return null

  return (
    <Popup
      onCancel={onClose}
      title={
        <div className="taskTabs">
          <ToggleButtonGroup type="radio" name="taskTabs_name" value={effectiveTab}>
            <ToggleButton
              title={t("viewAnalysisSamples")}
              variant="primary"
              value="SAMPLE"
              id="analysis-sample-view_id"
              className="task-tab-btn"
              onChange={() => setActiveTab("SAMPLE")}
            >
              {t("viewAnalysisSamples")}
            </ToggleButton>
            <ToggleButton
              title={t("editAnalysisTask")}
              variant="primary"
              value="TASK"
              id="analysis-task-view_id"
              className="task-tab-btn"
              onChange={() => setActiveTab("TASK")}
            >
              {t("editAnalysisTask")}
            </ToggleButton>
            {showQcTab && (
              <ToggleButton
                title={t("limsResultEntryTab")}
                variant="primary"
                value="QC"
                id="analysis-qc-view_id"
                className="task-tab-btn"
                onChange={() => setActiveTab("QC")}
              >
                {t("limsResultEntryTab")}
              </ToggleButton>
            )}
          </ToggleButtonGroup>
        </div>
      }
    >
      {loading && <Loading />}
      {/* SPEC-060 (#4132): the TASK and QC tabs are ONE AnalysisTaskConfig in one position with one
          key, differing only by the `sections` prop — so switching between them keeps the same
          instance and never loses an unsaved consumable or instrument choice. Switching to the
          SAMPLE tab does unmount it, exactly as it always has. */}
      {!loading && effectiveTab !== "SAMPLE" && (
        /* RR7 verify follow-up (#1): key on the analysis-task id so switching to ANOTHER analysis
           while this popup stays open REMOUNTS the form rather than re-rendering the same instance.
           Without it every useState initialised from the task — selected consumables (reagentLotIds),
           the reagentSeeded ref, name, dates, asset requirements, … — keeps the PREVIOUS analysis's
           values on the next one. For consumables that is a GMP data-integrity hazard (analysis B's
           result could be signed with analysis A's reagent/standard lots). The id is stable across the
           SPEC-028 snapshot refetch, so this never remounts spuriously — only on a real task switch. */
        <AnalysisTaskConfig
          key={taskId}
          task={task}
          sections={effectiveTab === "QC" ? "qc" : "task"}
          cancelHandler={onClose}
          submitHandler={(res) =>
            dispatch(analysisTasks.edit(taskId, res)).then(() => {
              if (task.analysisId) dispatch(analyses.setMovable(task.analysisId))
              onClose()
            })
          }
          deleteHandler={() => dispatch(analyses.remove(task.analysisId)).then(onClose)}
          bookedAndFreeAssets={bookedAndFreeAssets}
          disabled={disabled}
        />
      )}
      {!loading && effectiveTab === "SAMPLE" && (
        <SampleTableForPlannedAnalysis analysisId={task.analysisId} cancelHandler={onClose} readOnly={disabled} />
      )}
    </Popup>
  )
}
